import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';

/**
 * OAuth callback handler for the Platform Administration Console.
 *
 * browser-token-storage-hardening (.claude/rules/no-browser-token-storage.md):
 * the authorization service redirects here with a one-time `?code=` in the query
 * string (NOT tokens in the URL hash). We POST the code to
 * /api/v1/auth/exchange, which sets the HttpOnly session cookie and returns no
 * tokens; the user is then hydrated via GET /api/v1/auth/me. The page JS never
 * receives a bearer token.
 *
 * Expected query format:
 *   /auth/callback?code=<opaque single-use code>[&state=<csrf nonce>]
 *
 * NOTE: the matching aaim-core oauth2/callback redirect (hash -> ?code=) is the
 * lead's atomic step; this reader is authored now so the lead can flip aaim-core
 * and all four institutional SPAs together.
 */
export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const { completeOAuthExchange } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (!code) {
      setError('No authentication code received.');
      return;
    }

    completeOAuthExchange(code)
      .then(() => {
        window.history.replaceState(null, '', '/auth/callback');
        sessionStorage.removeItem('oauth_state');
        navigate('/', { replace: true });
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to process authentication response.');
      });
  }, [completeOAuthExchange, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-surface rounded shadow-sm border border-recessed p-8 text-center">
          <h1 className="text-xl font-sans font-semibold text-primary mb-4">
            Authentication Error
          </h1>
          <p className="text-sm text-tertiary mb-6">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="px-6 py-2 bg-accent text-white text-sm font-medium rounded hover:bg-accent/90 transition-colors"
          >
            Return to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-tertiary">Completing sign-in...</p>
      </div>
    </div>
  );
}
